import { actions } from "@app/utils";
import { createApiClient } from "@app/utils/apiClient";
import {
  RequestQueue,
  getOrCreate,
  getRetryAfterMs,
} from "@app/utils/requestQueue";
import { backgroundDelay } from "@app/utils/workerTimer";

import { uploadFileByURL } from "./s3";

import { IMS } from "./imsService";

export const DEFAULT_FIREFLY_MODEL = "image4_ultra";

/**
 * Parallel HTTP requests allowed on the Firefly client. It has to cover the
 * generate requests *and* the status polling/upload traffic they produce.
 * `createApiClient` queues anything above this limit and backs every endpoint
 * off for the `Retry-After` window when the server answers `429`.
 */
const FIREFLY_MAX_CONCURRENT_REQUESTS = 8;

export const fireflyClient = createApiClient(
  actions["exd-storyteller-ai/firefly"],
  FIREFLY_MAX_CONCURRENT_REQUESTS,
  5,
);

/**
 * Generation *jobs* allowed in parallel, per generate endpoint. Every model
 * that maps to the same endpoint (e.g. all `nano-banana*` models post to
 * `/generate-3p`) shares the same budget, since the rate limit is enforced by
 * the endpoint, not by the model.
 *
 * A job keeps its slot until it stops polling, so this caps how many jobs the
 * backend has to track for us at any given time — the per-request limit and
 * the `429` back-off are handled by the client itself.
 */
export const MAX_CONCURRENT_GENERATIONS = {
  "/generate-3p": 2,
  "/generate-video": 2,
  default: 4,
};

/** Upper bound for a `Retry-After` driven poll delay. */
const MAX_POLL_DELAY_MS = 30000;

const generateQueues = new Map();

/**
 * Returns the shared job queue for a generate endpoint.
 *
 * @param {string} generatePath - The generate endpoint, e.g. `/generate-3p`.
 * @returns {RequestQueue}
 */
export const getGenerateQueue = (generatePath) =>
  getOrCreate(
    generateQueues,
    generatePath,
    (path) =>
      new RequestQueue({
        name: `firefly${path}`,
        maxConcurrent:
          MAX_CONCURRENT_GENERATIONS[path] ?? MAX_CONCURRENT_GENERATIONS.default,
      }),
  );

/**
 * Maps a model version to its Firefly generate endpoint.
 *
 * @param {string} modelVersion - The `modelVersion` of the request body.
 * @returns {string} - The endpoint path.
 */
export const getGeneratePath = (modelVersion) => {
  switch (modelVersion) {
    case "image5":
      return "/generate-v5";
    case "nano-banana":
    case "nano-banana-2":
    case "nano-banana-3":
      return "/generate-3p";
    case "video1_standard":
    case "video1_8_standard":
      return "/generate-video";
    default:
      return "/generate";
  }
};

// export const firefly5Client = createApiClient(
//   actions["exd-storyteller-ai/firefly"],
//   1,
// );

// export const fireflyRawClient = createApiClient(
//   actions["exd-storyteller-ai/firefly"],
//   1,
// );

/**
 * Fetches a Firefly image based on the provided parameters.
 *
 * @param {string} storyId - The ID of the story.
 * @param {object} ims - The IMS object containing user information.
 * @param {*} body - The request body for the Firefly API.
 *
 * @returns {Promise<Object>} - A promise that resolves with the Firefly image response.
 */
export const fetchFireflyImage = async (storyId, body) => {
  const images = [];
  const email = IMS.userProfile?.email;
  const basePath = email
    ? `${email.split("@")[0]}/stories/${storyId}/firefly/`
    : null;
  const defaultS3FilePath = basePath
    ? `${basePath}${self.crypto.randomUUID()}.jpg`
    : undefined;

  // Step 1: Generate and extract images from Firefly API (persisting to S3 during poll when possible)
  const { outputs, images: legacyImages } = await generateImage({
    ...body,
    ...(defaultS3FilePath ? { s3FilePath: defaultS3FilePath } : {}),
  });
  const fireflyImages = outputs ?? legacyImages;

  // Step 2: Use backend-persisted S3 data when available, or upload remaining images to S3
  if (fireflyImages?.length > 0) {
    const s3UploadPromises = fireflyImages.map((image) => {
      if (image.filePath && image.presignURL) {
        return Promise.resolve({
          filePath: image.filePath,
          presignURL: image.presignURL,
        });
      }
      const filePath = `${basePath}${self.crypto.randomUUID()}.jpg`;
      return uploadFileByURL(filePath, image.url);
    });

    const s3Files = await Promise.all(s3UploadPromises);

    s3Files.forEach((file) => {
      // Response structure: { success: true, data: { filePath, presignURL } }
      const fileData = file.data || file; // Support both wrapped and unwrapped responses
      images.push({ url: fileData.presignURL, path: fileData.filePath });
    });
  }

  return images;
};

/**
 * Fetches persona images based on the provided parameters.
 *
 * @param {string} storyId - The ID of the story.
 * @param {object} persona - The persona object containing prompts.
 * @param {object} persona.avatarPrompt - The prompt for the avatar image.
 *
 * @returns {Promise<Array>} - A promise that resolves with an array of persona images.
 */
export const fetchPersonaImages = async (storyId, persona, imageReference) => {
  const { avatarPrompt } = persona;

  return Promise.all([
    fetchFireflyImage(storyId, {
      model: DEFAULT_FIREFLY_MODEL,
      prompt: avatarPrompt,
      negativePrompt:
        "texts on the image, blurry, low quality, bad lighting, bad composition",
      contentClass: "photo",
      size: { width: 2048, height: 2048 },
      visualIntensity: 8,
      detailLevel: "full",
      style: {
        presets: ["closeup"],
        strength: 50,
        ...(imageReference
          ? {
              imageReference: {
                source: {
                  ...(imageReference.url ? { url: imageReference.url } : {}),
                  ...(imageReference.uploadId
                    ? { uploadId: imageReference.uploadId }
                    : {}),
                },
              },
            }
          : {}),
      },
    }),
  ]);
};

/**
 * Uploads a Firefly image to the server.
 *
 * @param {string} filePath - The file path of the image to upload.
 * @param {object} [options] - Request options (e.g. signal for abort).
 *
 * @returns {Promise<Object>} - A promise that resolves with the upload response.
 */
export const uploadFireflyImage = async (filePath, options = {}) => {
  const response = await fireflyClient.post("/upload", { filePath }, options);

  if (!response.ok) {
    throw new Error("Failed to upload Firefly image", { cause: response });
  }

  return response.data;
};

/**
 * Polls the status of a Firefly image generation job.
 *
 * @param {object} params - Poll parameters.
 * @param {string} params.statusUrl - The status URL from the generate response.
 * @param {boolean} [params.shouldSave] - For V5: whether to save to Creative Cloud.
 * @param {string} [params.modelVersion] - "image5" for V5 endpoint, otherwise uses standard poll.
 * @param {object} [options] - Request options (e.g. signal for abort).
 * @returns {Promise<Object>} - The full response (ok, status, data, headers). Caller should check response.ok; 429 is allowed for rate-limit retry.
 */
export const pollStatus = async (
  { statusUrl, shouldSave, modelVersion, s3FilePath } = {},
  options = {},
) => {
  const pollPath =
    modelVersion === "image5" ? "/poll-status-v5" : "/poll-status";
  const pollBody = {
    statusUrl,
    ...(modelVersion === "image5"
      ? { shouldSave: shouldSave === true || shouldSave === "true" }
      : {}),
    ...(s3FilePath ? { s3FilePath } : {}),
  };

  return fireflyClient.post(pollPath, pollBody, options);
};

/**
 * Generates an image/video and polls until the job produced its outputs.
 *
 * The whole job (generate request + polling) holds a slot in the queue of its
 * generate endpoint, so at most {@link MAX_CONCURRENT_GENERATIONS} generations
 * run in parallel per endpoint. Callers never have to handle the rate limit
 * themselves: extra calls wait for a free slot, and a `429` on the generate
 * request is retried by the client after the `Retry-After` window, during
 * which every queued request to that endpoint is held back.
 *
 * @param {object} body - The generation payload (must contain `modelVersion`).
 * @param {object} [options] - Request options (e.g. signal for abort).
 * @param {Function} [statusCallback] - Called with each intermediate job status.
 * @returns {Promise<Object>} - `{ success, outputs }`.
 */
export const generateImage = async (
  body,
  options = {},
  statusCallback = null,
) => {
  const generatePath = getGeneratePath(body.modelVersion);

  return getGenerateQueue(generatePath).run(
    () => runGenerateJob(generatePath, body, options, statusCallback),
    { signal: options.signal },
  );
};

const runGenerateJob = async (generatePath, body, options, statusCallback) => {
  const { s3FilePath, ...generateBody } = body ?? {};
  const generateResponse = await fireflyClient.post(
    generatePath,
    generateBody,
    options,
  );

  if (!generateResponse.ok) {
    throw new Error("Failed to generate Firefly image", {
      cause: generateResponse,
    });
  }

  const statusUrl =
    generateResponse.data?.links?.result?.href ??
    generateResponse.data?.statusUrl;

  if (!statusUrl) {
    throw new Error("Missing statusUrl in generate response", {
      cause: generateResponse,
    });
  }

  // Step 2: Poll for job status using statusUrl in a loop until success
  let jobResult = null;
  let attempts = 0;
  const maxAttempts = 120;
  const initialPollDelay = 2000; // Wait 2s before first poll since generation never finishes in <2s
  const pollDelay = 1500; // 1.5s interval

  const shouldSave = body.shouldSave === true || body.shouldSave === "true";

  await backgroundDelay(initialPollDelay, { signal: options.signal });

  while (attempts < maxAttempts) {
    attempts++;

    const pollResponse = await pollStatus(
      { statusUrl, shouldSave, modelVersion: body.modelVersion, s3FilePath },
      options,
    );

    if (!pollResponse.ok && pollResponse.status !== 429) {
      throw new Error("Failed to poll job status", { cause: pollResponse });
    }

    jobResult = pollResponse.data;

    if (
      jobResult?.outputs?.length > 0 ||
      jobResult?.result?.outputs?.length > 0
    ) {
      // Job is complete when outputs are present
      break;
    }

    if (jobResult?.progress) {
      console.log(
        `Job processing... progress: ${jobResult.progress}% (attempt ${attempts}/${maxAttempts})`,
      );
    } else if (jobResult?.status) {
      console.log(
        `Job processing... status: ${jobResult.status} (attempt ${attempts}/${maxAttempts})`,
      );
    }

    statusCallback?.(jobResult);

    const delay = Math.max(
      pollDelay,
      Math.min(getRetryAfterMs(pollResponse) ?? 0, MAX_POLL_DELAY_MS),
    );

    // Wait before polling again (worker-backed)
    await backgroundDelay(delay, { signal: options.signal });
  }

  if (attempts >= maxAttempts) {
    throw new Error(`Job timed out after ${attempts} attempts`);
  }

  // Step 3: Extract outputs from job result
  const outputs = [];

  for (const output of jobResult?.outputs ?? jobResult?.result?.outputs ?? []) {
    const s3Data = output?.s3 ?? jobResult?.s3 ?? null;
    const imageUrl = output?.image?.presignedUrl ?? output?.image?.url;

    if (imageUrl) {
      outputs.push({
        kind: "image",
        url: s3Data?.presignURL ?? imageUrl,
        filePath: s3Data?.filePath ?? null,
        presignURL: s3Data?.presignURL ?? null,
        seed: output?.seed ?? 0, // Default seed to 0 if not provided
        fireflyId: output?.image?.id ?? null, // Include image ID if available
        creativeCloudFileId: output?.image?.creativeCloudFileId ?? null, // Include CC file ID if available
      });
    }

    const videoUrl = output?.video?.presignedUrl ?? output?.video?.url;

    if (videoUrl) {
      outputs.push({
        kind: "video",
        url: s3Data?.presignURL ?? videoUrl,
        filePath: s3Data?.filePath ?? null,
        presignURL: s3Data?.presignURL ?? null,
        seed: output?.seed ?? 0, // Default seed to 0 if not provided
        fireflyId: output?.video?.id ?? null, // Include video ID if available
        creativeCloudFileId: output?.video?.creativeCloudFileId ?? null, // Include CC file ID if available
      });
    }
  }

  // Check if we have at least one output
  if (outputs.length === 0) {
    throw new Error("No valid outputs were found in the response");
  }

  return {
    success: true,
    outputs,
  };
};
